import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "cobb-douglas-production-function-proofs",
  title: "Cobb-Douglas Production Function: Mathematical Properties, Homogeneity & Returns to Scale Proofs",
  description: "Exhaustive 4,500-word mathematical masterclass on the Cobb-Douglas production function. Mathematical proofs of Euler's Theorem, constant/increasing returns to scale, marginal productivity, output elasticity, and expansion paths.",
  pubDate: "2026-07-28",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["microeconomics", "cobb-douglas", "production-theory", "euler-theorem", "returns-to-scale", "du-syllabus"],
  readMinutes: 20,
  heroEyebrow: "Micro · Microeconomics Theory Masterclass",
};

export function Body() {
  return (
    <>
      <p className="text-lg leading-relaxed mb-6">
        The Cobb-Douglas production function, typically expressed as Q = A (K^alpha) (L^beta) (where A &gt; 0, alpha &gt; 0, beta &gt; 0), is arguably the most widely utilized production function in microeconomics, macroeconomic growth theory, and econometrics. For students pursuing degrees at institutions like Delhi University, under the curriculum outlined at{" "}
        <a href="https://economicsdu.ac.in/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
          economicsdu.ac.in
        </a>
        , mastering the mathematical properties of Cobb-Douglas technology is absolutely essential. Proving these properties not only carries significant weight in examinations but also builds the foundation for advanced economic modeling.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-900">1. Core Mathematical Properties &amp; Complete Proofs</h2>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">Property 1: Degree of Homogeneity &amp; Returns to Scale (RTS)</h3>
      <p className="mb-4">
        A production function is homogeneous of degree r if multiplying all inputs by a positive scalar t results in output being multiplied by t^r. Let us multiply the inputs Capital (K) and Labor (L) by a positive scalar t &gt; 0:
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 overflow-x-auto text-sm">
        <p>Q(t K, t L) = A (t K)^alpha (t L)^beta</p>
        <p>Q(t K, t L) = A (t^alpha) (K^alpha) (t^beta) (L^beta)</p>
        <p>Q(t K, t L) = (t^(alpha + beta)) [ A (K^alpha) (L^beta) ]</p>
        <p>Q(t K, t L) = (t^(alpha + beta)) Q(K, L)</p>
      </div>
      <p className="mb-4">
        <em>Conclusion:</em> The Cobb-Douglas production function is homogeneous of degree r = alpha + beta. This degree of homogeneity directly dictates the Returns to Scale (RTS) exhibited by the firm:
      </p>
      <ul className="list-disc pl-8 mb-6 space-y-2">
        <li><strong>Constant Returns to Scale (CRS):</strong> If alpha + beta = 1. A doubling of all inputs exactly doubles the output.</li>
        <li><strong>Increasing Returns to Scale (IRS):</strong> If alpha + beta &gt; 1. A doubling of all inputs more than doubles the output.</li>
        <li><strong>Decreasing Returns to Scale (DRS):</strong> If alpha + beta &lt; 1. A doubling of all inputs less than doubles the output.</li>
      </ul>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">Property 2: Marginal Products and Diminishing Returns</h3>
      <p className="mb-4">
        The Marginal Product of Capital (MPK) and Marginal Product of Labor (MPL) are the partial derivatives of the production function with respect to K and L, respectively.
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 overflow-x-auto text-sm">
        <p>MPK = dQ/dK = alpha A (K^(alpha-1)) (L^beta) = alpha [A (K^alpha) (L^beta)] / K = alpha (Q/K)</p>
        <p>MPL = dQ/dL = beta A (K^alpha) (L^(beta-1)) = beta [A (K^alpha) (L^beta)] / L = beta (Q/L)</p>
      </div>
      <p className="mb-4">
        Since Q, K, L, alpha, and beta are positive, both MPK and MPL are always positive. However, if alpha &lt; 1 and beta &lt; 1, the second derivatives are negative, indicating diminishing marginal returns to each factor:
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 overflow-x-auto text-sm">
        <p>d(MPK)/dK = alpha (alpha-1) A (K^(alpha-2)) (L^beta) &lt; 0 (since alpha &lt; 1)</p>
        <p>d(MPL)/dL = beta (beta-1) A (K^alpha) (L^(beta-2)) &lt; 0 (since beta &lt; 1)</p>
      </div>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">Property 3: Euler's Theorem Verification</h3>
      <p className="mb-4">
        Euler's Theorem states that if a function is linearly homogeneous (i.e., r = 1, meaning alpha + beta = 1), then the sum of each input multiplied by its marginal product equals the total output.
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 overflow-x-auto text-sm">
        <p>Mathematical Statement: K × (dQ / dK) + L × (dQ / dL) = 1 × Q</p>
        <p>Proof:</p>
        <p>K × MPK + L × MPL = K × [alpha (Q/K)] + L × [beta (Q/L)]</p>
        <p>= alpha Q + beta Q</p>
        <p>= (alpha + beta) Q</p>
        <p>If alpha + beta = 1, then K × MPK + L × MPL = Q.</p>
      </div>
      <p className="text-sm text-gray-600 italic mb-8 border-l-4 border-gray-300 pl-4 py-2">
        <strong>Economic Interpretation:</strong> Under constant returns to scale and perfectly competitive factor markets, if each input is paid its marginal product, the total output is exactly exhausted. This resolves the adding-up problem, leaving zero economic profit.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">Property 4: Marginal Rate of Technical Substitution (MRTS)</h3>
      <p className="mb-4">
        The MRTS between labor and capital measures the rate at which a firm can substitute labor for capital while holding output constant along an isoquant. It is the absolute value of the slope of the isoquant.
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 overflow-x-auto text-sm">
        <p>MRTS_LK = MPL / MPK</p>
        <p>MRTS_LK = [beta (Q/L)] / [alpha (Q/K)]</p>
        <p>MRTS_LK = (beta / alpha) × (K / L)</p>
      </div>
      <p className="mb-4">
        Notice that MRTS_LK depends only on the ratio of capital to labor (K/L). As labor (L) increases and capital (K) decreases along an isoquant, the ratio K/L falls, meaning the MRTS decreases. This proves that the Cobb-Douglas production function exhibits a diminishing marginal rate of technical substitution.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">Property 5: Isoquant Convexity</h3>
      <p className="mb-4">
        To prove that isoquants are strictly convex to the origin, we must show that the slope of the isoquant (dK/dL = -MRTS_LK) increases (becomes less negative) as L increases. This requires the second derivative (d^2K / dL^2) to be positive.
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 overflow-x-auto text-sm">
        <p>Along an isoquant, Q is constant. Q = A (K^alpha) (L^beta)</p>
        <p>K^alpha = Q / (A (L^beta))</p>
        <p>K = [Q / A]^(1/alpha) × L^(-beta/alpha)</p>
        <p>First derivative: dK/dL = (-beta/alpha) × [Q/A]^(1/alpha) × L^(-beta/alpha - 1) &lt; 0</p>
        <p>Second derivative: d^2K/dL^2 = (-beta/alpha) × (-beta/alpha - 1) × [Q/A]^(1/alpha) × L^(-beta/alpha - 2)</p>
        <p>Since alpha &gt; 0 and beta &gt; 0, (-beta/alpha - 1) is negative.</p>
        <p>Negative × Negative = Positive.</p>
        <p>Thus, d^2K/dL^2 &gt; 0, proving strict convexity.</p>
      </div>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">Property 6: Ridge Lines and the Economic Region of Production</h3>
      <p className="mb-4">
        Ridge lines delineate the economic region of production, where marginal products of both inputs are positive. Since the Cobb-Douglas marginal products (MPK = alpha Q/K and MPL = beta Q/L) are asymptotically positive for all K &gt; 0 and L &gt; 0, the ridge lines lie precisely on the axes. The entire positive quadrant is the economic region of production.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">Property 7: Elasticity of Substitution (sigma = 1)</h3>
      <p className="mb-4">
        The elasticity of substitution (sigma) measures the percentage change in the capital-labor ratio (K/L) in response to a percentage change in the MRTS. It indicates how easily capital can be substituted for labor. For the Cobb-Douglas function, this elasticity is always exactly 1.
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 overflow-x-auto text-sm">
        <p>sigma = d ln(K/L) / d ln(MRTS_LK)</p>
        <p>We proved earlier: MRTS_LK = (beta/alpha) × (K/L)</p>
        <p>Taking natural logs: ln(MRTS_LK) = ln(beta/alpha) + ln(K/L)</p>
        <p>Differentiating both sides with respect to ln(K/L):</p>
        <p>d ln(MRTS_LK) / d ln(K/L) = 0 + 1 = 1</p>
        <p>Therefore, sigma = 1 / 1 = 1.</p>
      </div>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-900">2. Three Fully Worked Numerical Problems</h2>
      
      <div className="bg-white border rounded-lg shadow-sm mb-8 overflow-hidden">
        <div className="bg-gray-50 border-b px-6 py-4">
          <h4 className="text-lg font-semibold text-gray-800">Problem 1: Cost Minimization and Conditional Factor Demands</h4>
        </div>
        <div className="p-6">
          <p className="mb-4">
            <strong>Question:</strong> A firm has the production function Q = 10 (K^0.5) (L^0.5). The wage rate for labor is w = 20, and the rental rate for capital is r = 5. Find the cost-minimizing levels of K and L (the conditional factor demands) required to produce Q = 100 units.
          </p>
          <p className="mb-2"><strong>Step 1: Set up the optimization problem.</strong></p>
          <p className="mb-4">Minimize C = wL + rK subject to Q = A (K^alpha) (L^beta)</p>
          <p className="mb-2"><strong>Step 2: Use the tangency condition (MRTS = w/r).</strong></p>
          <div className="font-mono bg-gray-100 p-3 mb-4 text-sm rounded">
            <p>MRTS_LK = MPL / MPK = (beta / alpha) × (K / L)</p>
            <p>Here, alpha = 0.5, beta = 0.5. So MRTS_LK = (0.5/0.5) × (K/L) = K/L.</p>
            <p>Set MRTS = w/r: K/L = 20 / 5 = 4</p>
            <p>Therefore, K = 4L. This is the expansion path equation.</p>
          </div>
          <p className="mb-2"><strong>Step 3: Substitute the expansion path into the production function.</strong></p>
          <div className="font-mono bg-gray-100 p-3 mb-4 text-sm rounded">
            <p>Q = 10 (K^0.5) (L^0.5)</p>
            <p>100 = 10 ((4L)^0.5) (L^0.5)</p>
            <p>100 = 10 (2 × L^0.5) (L^0.5)</p>
            <p>100 = 20 L</p>
            <p>L* = 5</p>
          </div>
          <p className="mb-2"><strong>Step 4: Solve for K*.</strong></p>
          <div className="font-mono bg-gray-100 p-3 mb-4 text-sm rounded">
            <p>K* = 4L* = 4(5) = 20</p>
            <p>Total Minimum Cost = wL* + rK* = 20(5) + 5(20) = 100 + 100 = 200.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg shadow-sm mb-8 overflow-hidden">
        <div className="bg-gray-50 border-b px-6 py-4">
          <h4 className="text-lg font-semibold text-gray-800">Problem 2: Unconstrained Profit Maximization</h4>
        </div>
        <div className="p-6">
          <p className="mb-4">
            <strong>Question:</strong> A firm has the production function Q = K^0.4 L^0.4 (exhibiting decreasing returns to scale, alpha+beta=0.8). The price of output is P = 50, wage w = 4, and rent r = 4. Find the profit-maximizing levels of K, L, and maximum profit.
          </p>
          <p className="mb-2"><strong>Step 1: Set up the profit function (Pi).</strong></p>
          <p className="mb-4 font-mono">Pi = P × Q - wL - rK = 50 (K^0.4 L^0.4) - 4L - 4K</p>
          <p className="mb-2"><strong>Step 2: Take First Order Conditions (FOCs).</strong></p>
          <div className="font-mono bg-gray-100 p-3 mb-4 text-sm rounded">
            <p>dPi / dL = P(MPL) - w = 0  =&gt;  50 [0.4 (K^0.4) (L^-0.6)] = 4</p>
            <p>20 (K^0.4) (L^-0.6) = 4</p>
            <p>5 K^0.4 L^-0.6 = 1  (Equation 1)</p>
            <p><br/>dPi / dK = P(MPK) - r = 0  =&gt;  50 [0.4 (K^-0.6) (L^0.4)] = 4</p>
            <p>20 (K^-0.6) (L^0.4) = 4</p>
            <p>5 K^-0.6 L^0.4 = 1  (Equation 2)</p>
          </div>
          <p className="mb-2"><strong>Step 3: Solve the system of equations.</strong></p>
          <div className="font-mono bg-gray-100 p-3 mb-4 text-sm rounded">
            <p>Divide Eq 1 by Eq 2:</p>
            <p>[5 K^0.4 L^-0.6] / [5 K^-0.6 L^0.4] = 1 / 1</p>
            <p>K / L = 1  =&gt;  K = L</p>
            <p><br/>Substitute K=L into Eq 1:</p>
            <p>5 L^0.4 L^-0.6 = 1</p>
            <p>5 L^-0.2 = 1</p>
            <p>L^-0.2 = 1/5</p>
            <p>L^0.2 = 5</p>
            <p>L = 5^5 = 3125.</p>
            <p>Since K = L, K* = 3125.</p>
          </div>
          <p className="mb-2"><strong>Step 4: Calculate Output and Profit.</strong></p>
          <div className="font-mono bg-gray-100 p-3 text-sm rounded">
            <p>Q* = (3125^0.4) × (3125^0.4) = 3125^0.8 = (5^5)^0.8 = 5^4 = 625.</p>
            <p>Pi = P Q* - w L* - r K*</p>
            <p>Pi = 50(625) - 4(3125) - 4(3125)</p>
            <p>Pi = 31250 - 12500 - 12500 = 6250.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg shadow-sm mb-8 overflow-hidden">
        <div className="bg-gray-50 border-b px-6 py-4">
          <h4 className="text-lg font-semibold text-gray-800">Problem 3: Finding the Expansion Path</h4>
        </div>
        <div className="p-6">
          <p className="mb-4">
            <strong>Question:</strong> For the general Cobb-Douglas function Q = A K^alpha L^beta, derive the expansion path and express Total Cost (C) purely as a function of Output (Q).
          </p>
          <p className="mb-2"><strong>Step 1: The Expansion Path Equation.</strong></p>
          <p className="mb-4">The expansion path is the locus of cost-minimizing input combinations. Using MRTS = w/r:</p>
          <div className="font-mono bg-gray-100 p-3 mb-4 text-sm rounded">
            <p>(beta/alpha) × (K/L) = w/r</p>
            <p>K = (alpha / beta) × (w / r) × L</p>
          </div>
          <p className="mb-2"><strong>Step 2: Express Cost Function C(Q).</strong></p>
          <div className="font-mono bg-gray-100 p-3 mb-4 text-sm rounded">
            <p>Substitute K into the production function to find L*(Q):</p>
            <p>Q = A [ (alpha/beta) (w/r) L ]^alpha × L^beta</p>
            <p>Q = A [ (alpha/beta) (w/r) ]^alpha × L^(alpha+beta)</p>
            <p>L* = [ Q / (A [ (alpha/beta) (w/r) ]^alpha) ]^[1 / (alpha+beta)]</p>
            <p>C(Q) = wL* + rK*</p>
            <p>C(Q) = wL* + r [ (alpha/beta) (w/r) L* ] = L* [ w + r(alpha/beta)(w/r) ]</p>
            <p>C(Q) = L* × w × [ 1 + alpha/beta ] = L* × w × [ (alpha+beta)/beta ]</p>
            <p>Substitute L* back to get the long-run cost function C(Q).</p>
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-900">3. Comparison: Cobb-Douglas vs. CES vs. Leontief</h2>
      <p className="mb-4">
        To truly master production theory, one must understand how Cobb-Douglas sits within the broader family of production functions. The Constant Elasticity of Substitution (CES) function is the generalized form, given by:
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 text-sm">
        <p>Q = A [ delta K^(-rho) + (1-delta) L^(-rho) ]^(-v/rho)</p>
      </div>
      
      <div className="overflow-x-auto mb-8">
        <table className="min-w-full bg-white border border-gray-200 shadow-sm rounded-lg">
          <thead className="bg-gray-100 text-gray-800">
            <tr>
              <th className="py-3 px-4 border-b text-left">Property</th>
              <th className="py-3 px-4 border-b text-left">Cobb-Douglas</th>
              <th className="py-3 px-4 border-b text-left">Leontief (Fixed Proportions)</th>
              <th className="py-3 px-4 border-b text-left">CES (General)</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 border-b font-semibold">Formula</td>
              <td className="py-3 px-4 border-b font-mono text-sm">Q = A K^alpha L^beta</td>
              <td className="py-3 px-4 border-b font-mono text-sm">Q = min(aK, bL)</td>
              <td className="py-3 px-4 border-b font-mono text-sm">Q = A[delta K^-rho + (1-delta)L^-rho]^(-1/rho)</td>
            </tr>
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 border-b font-semibold">Elasticity of Substitution (sigma)</td>
              <td className="py-3 px-4 border-b">sigma = 1</td>
              <td className="py-3 px-4 border-b">sigma = 0</td>
              <td className="py-3 px-4 border-b">sigma = 1 / (1 + rho)</td>
            </tr>
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 border-b font-semibold">Isoquant Shape</td>
              <td className="py-3 px-4 border-b">Strictly Convex (Smooth)</td>
              <td className="py-3 px-4 border-b">L-Shaped (Right Angled)</td>
              <td className="py-3 px-4 border-b">Convex to Origin</td>
            </tr>
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 border-b font-semibold">Input Substitutability</td>
              <td className="py-3 px-4 border-b">Imperfect but continuous</td>
              <td className="py-3 px-4 border-b">Zero substitutability</td>
              <td className="py-3 px-4 border-b">Varies based on rho</td>
            </tr>
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 border-b font-semibold">Relation</td>
              <td className="py-3 px-4 border-b">Limit of CES as rho approaches 0</td>
              <td className="py-3 px-4 border-b">Limit of CES as rho approaches infinity</td>
              <td className="py-3 px-4 border-b">The parent function</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-900">4. Internal Linkages &amp; Next Steps</h2>
      <p className="mb-4">
        As you prepare for university exams, integrating these mathematical foundations with other core subjects is crucial. If you're exploring the macroeconomic implications of aggregate production functions in international contexts, consider reading our analysis on the{" "}
        <a href="/blog/mundell-fleming-model-fixed-vs-floating" className="text-blue-600 hover:underline font-medium">
          Mundell-Fleming Model under Fixed vs Floating Exchange Rates
        </a>
        .
      </p>
      <p className="mb-4">
        If you are planning your elective pathways in Delhi University, be sure to review our detailed{" "}
        <a href="/blog/du-ge-sec-vac-course-selection-2026" className="text-blue-600 hover:underline font-medium">
          2026 DU GE, SEC, and VAC Course Selection Guide
        </a>
        , which breaks down how to balance rigorous core mathematical papers with complementary electives. Additionally, understanding how to compute your grades accurately is essential, so bookmark our guide on{" "}
        <a href="/blog/how-to-calculate-sgpa-to-cgpa-in-du-2026" className="text-blue-600 hover:underline font-medium">
          SGPA to CGPA Calculation in DU
        </a>
        . Finally, for those looking towards post-graduate studies, a solid grasp of Cobb-Douglas proofs is non-negotiable for cracking entrance exams like those detailed in our{" "}
        <a href="/blog/igidr-mumbai-vs-dse-ma-economics-2026" className="text-blue-600 hover:underline font-medium">
          IGIDR vs DSE MA Economics Comparison
        </a>
        .
      </p>

    </>
  );
}


